import { create } from 'zustand';
import { createSelectors, withDevtools, StoreName } from '../config';
import { createInitialData, createDataActions } from '../types';
import { EntityName } from 'shared/domain/entityName';
import { siteClient } from 'shared/domain/site/siteClient';
import type { SiteModel } from 'shared/domain/site/types/model';
import { getProjectId } from 'store/project';
import { changeEntityDeleted, createEntityCollectionActions } from '../entityCollection';
import type { SitesStore } from './types';

const useSitesStoreBase = create<SitesStore>()(
  withDevtools((set, get) => {
    /** Archives or restores at once; undone when the server rejects it. */
    const changeSiteDeleted = (siteId: string, deleted: boolean): void =>
      changeEntityDeleted({
        deleted,
        markDeleted: (value) => get().actions.updateSiteDeleted(siteId, value),
        sendChange: (value) =>
          value ? siteClient.remove(siteId, getProjectId()) : siteClient.restore(siteId, getProjectId()),
        entityLabel: 'a site',
      });

    return {
      data: createInitialData<SiteModel>(),
      isReady: false,

      actions: {
        ...createDataActions<SiteModel, SitesStore>(set),
        ...createEntityCollectionActions({ client: siteClient, entityName: EntityName.sites }, () => get().actions),
        archive: (siteId) => changeSiteDeleted(siteId, true),
        restore: (siteId) => changeSiteDeleted(siteId, false),

        setItems: (items) =>
          set((state) => ({ ...state, data: { ...state.data, items, loading: false }, isReady: true })),
        reset: () => set((state) => ({ ...state, data: createInitialData<SiteModel>(), isReady: false })),

        updateSiteDeleted: (siteId, deleted) =>
          set((state) => ({
            ...state,
            data: {
              ...state.data,
              items: state.data.items.map((site) =>
                site._id === siteId ? { ...site, deleted } : site
              ),
            },
          })),
      },
    };
  }, StoreName.Sites)
);

export const useSitesStore = createSelectors(useSitesStoreBase);

export const getSitesStoreState = (): SitesStore => useSitesStore.getState();
